import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import { useNavIntent } from '../navIntent';
import PageShell, { Button } from '../../ui/PageShell';
import { formatPhone } from '../../ui/phone';
import Table from '../../ui/Table';
import Modal, { ConfirmModal } from '../../ui/Modal';
import { FormGrid, Field, Input, Select } from '../../ui/Form';
import PhoneInput from '../../ui/PhoneInput';
import NameField from '../../ui/NameField';
import HelpButton from '../help/HelpButton';
import EmptyGuide from '../help/EmptyGuide';

export default function Teachers() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(async () => {
    const [teachers, users] = await Promise.all([api.teachers(), api.users()]);
    return { teachers, users };
  }, []);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [lockTarget, setLockTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = useDirtyForm(form);

  // Lock state lives on the `users` row (role=teacher, person_id=teacher.id),
  // not on the teacher record, so the two are joined here.
  const loginByTeacher = new Map(
    (data?.users || []).filter((u) => u.role === 'teacher').map((u) => [u.person_id, u])
  );

  const rows = (data?.teachers || []).map((t) => {
    const login = loginByTeacher.get(t.id);
    return { ...t, is_locked: login?.is_locked || 0, login_user_id: login?.id || null };
  });

  const openNew = () => {
    const next = { name: '', phone: '', status: 'active' };
    setForm(next); dirty.reset(next); setEditing({});
  };
  const openEdit = (t) => {
    const next = { name: t.name, phone: t.phone || '', status: t.status };
    setForm(next); dirty.reset(next); setEditing(t);
  };

  // Navbar search → «بيانات المعلم»: open this teacher's form.
  useNavIntent('teachers', (it) => {
    if (!data) return false;
    const t = rows.find((r) => r.id === it.teacherId);
    if (t) openEdit(t);
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('الاسم مطلوب');
    setSaving(true);
    try {
      if (editing.id) { await api.updateTeacher(editing.id, form); toast.success('تم التحديث'); }
      else { await api.createTeacher(form); toast.success('تمت الإضافة'); }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.deleteTeacher(toDelete.id);
      toast.success('تم تعطيل المعلم');
      setToDelete(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      if (lockTarget.is_locked) await api.unlockUser(lockTarget.login_user_id);
      else await api.lockUser(lockTarget.login_user_id, 'إيقاف من لوحة الإدارة');
      toast.success(lockTarget.is_locked ? 'تم فك الإيقاف' : 'تم إيقاف الحساب');
      setLockTarget(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="المعلمون" description="إدارة المعلمين وحسابات دخولهم"
      actions={(
        <>
          <HelpButton guides={['teacher-add']} />
          <Button onClick={openNew} data-guide="teachers-add">+ معلم جديد</Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <div className="ui-notice ui-notice-warning">
        <div className="ui-notice-body">
          لا يمكن تعطيل معلم لديه توكيل نشط — انقل مواده إلى معلم آخر أولاً.
        </div>
      </div>

      <Table
        keyField="id"
        rows={rows}
        loading={loading}
        statusKey="status"
        emptyText={<EmptyGuide text="لا يوجد معلمون بعد" guide="teacher-add" />}
        searchPlaceholder="ابحث بالاسم أو الهاتف..."
        onEdit={openEdit}
        onDelete={(t) => setToDelete(t)}
        renderActions={(t) => t.login_user_id && (
          <button type="button" className={`ui-btn-mini${t.is_locked ? '' : ' ui-btn-mini-warn'}`}
            onClick={() => setLockTarget(t)}>
            {t.is_locked ? 'فك الإيقاف' : 'إيقاف'}
          </button>
        )}
        columns={[
          { key: 'name', label: 'الاسم', width: 2, filterable: false, render: (r) => <strong>{r.name}</strong> },
          { key: 'phone', label: 'الهاتف', nowrap: true, filterable: false, render: (r) => (r.phone ? <bdi dir="ltr">{formatPhone(r.phone)}</bdi> : '—') },
          { key: 'is_locked', label: 'حساب الدخول', nowrap: true, align: 'center', filterable: false,
            render: (r) => !r.login_user_id
              ? <span className="ui-badge ui-badge-neutral">بلا حساب</span>
              : <span className={`ui-badge ${r.is_locked ? 'ui-badge-danger' : 'ui-badge-success'}`}>
                  {r.is_locked ? 'موقوف' : 'نشط'}
                </span> },
          { key: 'status', label: 'الحالة', nowrap: true, align: 'center',
            badge: (v) => (v === 'active' ? 'success' : 'neutral'),
            formatValue: (v) => (v === 'active' ? 'نشط' : 'معطّل') },
        ]}
      />

      {editing && (
        <Modal open size="md" title={editing.id ? `تعديل: ${editing.name}` : 'معلم جديد'}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">حفظ</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <NameField
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                required
                guide="teacher-name"
                hint="يُحفظ اسماً واحداً كاملاً، كما في جدول المعلمين."
              />
              <Field label="الهاتف" hint="يُستخدم لإرسال رمز الدخول" guide="teacher-phone"><PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} /></Field>
              <Field label="الحالة" span>
                <Select value={form.status} onChange={set('status')}
                  options={[{ value: 'active', label: 'نشط' }, { value: 'inactive', label: 'معطّل' }]} />
              </Field>
            </FormGrid>
          </form>
        </Modal>
      )}

      <ConfirmModal
        open={!!toDelete}
        title="تعطيل المعلم"
        message={`سيتم تعطيل «${toDelete?.name || ''}» وإيقاف حساب دخوله. لن تُحذف بياناته أو سجلّه.`}
        confirmText="تعطيل"
        busy={busy}
        onCancel={() => setToDelete(null)}
        onConfirm={doDelete}
      />

      <ConfirmModal
        open={!!lockTarget}
        title={lockTarget?.is_locked ? 'فك إيقاف الحساب' : 'إيقاف الحساب'}
        tone={lockTarget?.is_locked ? 'success' : 'danger'}
        message={lockTarget?.is_locked
          ? `سيتمكن «${lockTarget?.name}» من تسجيل الدخول مجدداً.`
          : `لن يتمكن «${lockTarget?.name}» من تسجيل الدخول، وستُلغى جلساته الحالية.`}
        confirmText={lockTarget?.is_locked ? 'فك الإيقاف' : 'إيقاف'}
        busy={busy}
        onCancel={() => setLockTarget(null)}
        onConfirm={toggleLock}
      />
    </PageShell>
  );
}
